/**
 * BrandingTab Component
 * Configure widget branding (logo, colors, fonts)
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BrandingSchema, type BrandingFormData } from '../../schemas/widgetConfigSchema';
import { useUpdateBranding, useLogoUpload, useHeroImageUpload } from '../../hooks/useWidgetConfig';
import { FormSection, FormField } from './shared/FormSection';
import { ColorPicker } from './shared/ColorPicker';
import { ImageUpload } from './shared/ImageUpload';
import { Button } from '../ui/button';
import { FONT_FAMILIES } from '../../types/widget-config';
import type { WidgetConfiguration } from '../../types/widget-config';

export interface BrandingTabProps {
  config: WidgetConfiguration;
}

/**
 * BrandingTab allows providers to customize widget appearance
 */
export function BrandingTab({ config }: BrandingTabProps) {
  const updateBranding = useUpdateBranding();
  const logoUpload = useLogoUpload();
  const heroImageUpload = useHeroImageUpload();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<BrandingFormData>({
    resolver: zodResolver(BrandingSchema),
    defaultValues: config.branding,
  });

  const primaryColor = watch('primaryColor');
  const secondaryColor = watch('secondaryColor');

  const onSubmit = (data: BrandingFormData) => {
    updateBranding.mutate(data);
  };

  const handleLogoUpload = async (file: File) => {
    try {
      const result = await logoUpload.mutateAsync(file);
      setValue('logoUrl', result.logoUrl, { shouldDirty: true });
    } catch (error) {
      // Error toast already shown by mutation
    }
  };

  const handleHeroImageUpload = async (file: File) => {
    try {
      const result = await heroImageUpload.mutateAsync(file);
      setValue('heroImage', result.heroImageUrl, { shouldDirty: true });
    } catch (error) {
      // Error toast already shown by mutation
    }
  };

  const handleLogoRemove = () => {
    setValue('logoUrl', '', { shouldDirty: true });
  };

  const handleHeroImageRemove = () => {
    setValue('heroImage', '', { shouldDirty: true });
  };

  const isLoading = updateBranding.isPending || logoUpload.isPending || heroImageUpload.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Company Information */}
      <FormSection
        title="Company Information"
        description="Basic company details displayed on your widget"
      >
        <FormField
          label="Company Name"
          required
          error={errors.companyName?.message}
          htmlFor="companyName"
        >
          <input
            {...register('companyName')}
            id="companyName"
            type="text"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Paso Rolloff Services"
          />
        </FormField>

        <FormField
          label="Logo"
          description="Upload your company logo (PNG, JPG, SVG, or WebP)"
          error={errors.logoUrl?.message}
        >
          <ImageUpload
            value={watch('logoUrl')}
            onChange={handleLogoUpload}
            onRemove={handleLogoRemove}
            isUploading={logoUpload.isPending}
            recommendedDimensions="200x60px"
          />
        </FormField>
      </FormSection>

      {/* Colors */}
      <FormSection
        title="Brand Colors"
        description="Customize the color scheme of your widget"
      >
        <FormField
          label="Primary Color"
          description="Main brand color used for buttons and accents"
          required
          error={errors.primaryColor?.message}
        >
          <ColorPicker
            value={primaryColor}
            onChange={(color) => setValue('primaryColor', color, { shouldDirty: true })}
          />
        </FormField>

        <FormField
          label="Secondary Color"
          description="Optional accent color for highlights and hover states"
          error={errors.secondaryColor?.message}
        >
          <ColorPicker
            value={secondaryColor || ''}
            onChange={(color) => setValue('secondaryColor', color, { shouldDirty: true })}
            presets={[primaryColor, '#2c7a4f', '#3b9363', '#0066cc', '#4c1d95', '#7c2d12']}
          />
        </FormField>
      </FormSection>

      {/* Typography */}
      <FormSection
        title="Typography"
        description="Select the font family for your widget"
      >
        <FormField
          label="Font Family"
          error={errors.fontFamily?.message}
          htmlFor="fontFamily"
        >
          <select
            {...register('fontFamily')}
            id="fontFamily"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Default (Inter)</option>
            {FONT_FAMILIES.map((font) => (
              <option key={font} value={font} style={{ fontFamily: font }}>
                {font}
              </option>
            ))}
          </select>
        </FormField>
      </FormSection>

      {/* Hero Image */}
      <FormSection
        title="Hero Image (Optional)"
        description="Large banner image displayed at the top of your widget"
      >
        <FormField
          label="Hero Image"
          description="Recommended dimensions: 1200x400px"
          error={errors.heroImage?.message}
        >
          <ImageUpload
            value={watch('heroImage')}
            onChange={handleHeroImageUpload}
            onRemove={handleHeroImageRemove}
            isUploading={heroImageUpload.isPending}
            recommendedDimensions="1200x400px"
          />
        </FormField>
      </FormSection>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button
          type="submit"
          disabled={!isDirty || isLoading}
          className="min-w-[120px]"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Unsaved Changes Warning */}
      {isDirty && (
        <p className="text-sm text-amber-600" role="status">
          You have unsaved changes
        </p>
      )}
    </form>
  );
}
